import { useEffect, useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface Influencer {
  id: string; name: string; slug: string; status: string;
  fictionalAge: number; language: string; referenceAssetId?: string | null;
}

export function InfluencersPage() {
  const { role } = useAuth();
  const [list, setList] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", fictionalAge: 25, language: "es" });
  const [formError, setFormError] = useState("");
  const [refUrls, setRefUrls] = useState<Record<string, string>>({});
  const [familyCounts, setFamilyCounts] = useState<Record<string, number>>({});

  function load() {
    setLoading(true);
    api.get<{ influencers: Influencer[] }>("/influencers?limit=50")
      .then(async r => {
        setList(r.influencers);
        // Load reference photos and family counts in parallel
        const urlMap: Record<string, string> = {};
        const countMap: Record<string, number> = {};
        await Promise.allSettled(r.influencers.map(async inf => {
          // Reference photo URL
          if (inf.referenceAssetId) {
            try {
              const asset = await api.get<{ downloadUrl: string }>(`/assets/${inf.referenceAssetId}`);
              urlMap[inf.id] = asset.downloadUrl;
            } catch { /* ignore */ }
          }
          // Family member count
          try {
            const fam = await api.get<{ familyMembers: unknown[] }>(`/influencers/${inf.id}/family-members`);
            countMap[inf.id] = fam.familyMembers.length;
          } catch { /* ignore */ }
        }));
        setRefUrls(urlMap);
        setFamilyCounts(countMap);
      })
      .catch(() => setError("Error al cargar influencers"))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setFormError("");
    try {
      await api.post("/influencers", form);
      setCreating(false);
      setForm({ name: "", slug: "", fictionalAge: 25, language: "es" });
      load();
    } catch (err: unknown) {
      const e = err as { data?: { message?: string } };
      setFormError(e.data?.message || "Error al crear");
    }
  }

  const statusColor = (s: string) => s === "active" ? "#2d6a4f" : s === "paused" ? "#d97706" : s === "archived" ? "#9ca3af" : "#6366f1";

  if (loading) return <div style={{ color: "#888", padding: 24 }}>Cargando…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Influencers</h1>
          <p style={{ color: "#666", fontSize: 13, margin: "4px 0 0" }}>{list.length} personajes</p>
        </div>
        {(role === "owner" || role === "editor") && (
          <button onClick={() => setCreating(true)} style={{
            padding: "8px 16px", background: "#2d6a4f", color: "#fff",
            border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14
          }}>+ Nueva influencer</button>
        )}
      </div>

      {error && <div style={{ color: "#dc2626", marginBottom: 16 }}>{error}</div>}

      {creating && (
        <form onSubmit={handleCreate} style={{ background: "#fff", padding: 24, borderRadius: 10, marginBottom: 24, display: "flex", flexDirection: "column", gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Nueva influencer</h3>
          {formError && <div style={{ color: "#dc2626", fontSize: 13 }}>{formError}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
              Nombre <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inp} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
              Slug (URL) <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} required style={inp} placeholder="nombre-unico" />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
              Edad ficticia (≥25) <input type="number" min={25} value={form.fictionalAge} onChange={e => setForm({ ...form, fictionalAge: Number(e.target.value) })} required style={inp} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
              Idioma <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} style={inp}>
                <option value="es">Español</option>
                <option value="en">Inglés</option>
                <option value="ca">Catalán</option>
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={{ padding: "8px 20px", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Crear</button>
            <button type="button" onClick={() => setCreating(false)} style={{ padding: "8px 16px", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", background: "none" }}>Cancelar</button>
          </div>
        </form>
      )}

      {list.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 10, padding: 40, textAlign: "center", color: "#888" }}>
          Sin influencers. Crea la primera para empezar.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
          {list.map(inf => {
            const refUrl = refUrls[inf.id];
            const famCount = familyCounts[inf.id] ?? 0;
            return (
              <Link key={inf.id} to={`/influencers/${inf.id}`} style={{ textDecoration: "none" }}>
                <div
                  style={{ background: "#fff", borderRadius: 10, overflow: "hidden", transition: "box-shadow 0.15s" }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)")}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = "none")}
                >
                  {/* Reference photo banner */}
                  <div style={{
                    width: "100%", aspectRatio: "4/5", background: "#f3f4f6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden", position: "relative"
                  }}>
                    {refUrl ? (
                      <img src={refUrl} alt={inf.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }} />
                    ) : (
                      <span style={{ fontSize: 40, opacity: 0.3 }}>👤</span>
                    )}
                    {/* Status badge overlaid top-right */}
                    <span style={{
                      position: "absolute", top: 8, right: 8,
                      fontSize: 11, background: statusColor(inf.status) + "dd",
                      color: "#fff", padding: "2px 8px", borderRadius: 12, fontWeight: 600
                    }}>
                      {inf.status}
                    </span>
                  </div>

                  {/* Card body */}
                  <div style={{ padding: "12px 16px 14px" }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a", marginBottom: 4 }}>
                      {inf.name}
                    </div>
                    <div style={{ fontSize: 12, color: "#888" }}>
                      @{inf.slug} · {inf.fictionalAge} años · {inf.language.toUpperCase()}
                    </div>
                    {famCount > 0 && (
                      <div style={{ fontSize: 11, color: "#6366f1", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                        <span>👨‍👩‍👧</span>
                        <span>{famCount} familiar{famCount !== 1 ? "es" : ""}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inp: React.CSSProperties = { padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, outline: "none" };
